import { CSSProperties, useEffect, useId, useState } from 'react';
import type { Props as ModalProps } from 'react-modal';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styles from './SlipSubmitTimer.module.css';

interface Props {
  /**
   * Epoch ms when the submission started. Passing it keeps the count running
   * across a remount (the live flow toggles loading between the quote check
   * and ticket creation). Defaults to the moment the timer mounts.
   */
  startedAt?: number | null;
  /** Counted down from (owner, 2026-10-25: 5, 4, 3 ... instead of 1, 2, 3).
   * The count stops at 1 if the answer takes longer. */
  seconds?: number;
}

/**
 * Spread onto the react-modal while the timer shows. react-modal paints a
 * bordered box behind its content (setup/modalSetup.ts); the timer draws its
 * own card, so the box has to go.
 */
export const SLIP_SUBMIT_TIMER_MODAL_PROPS: Pick<ModalProps, 'style'> = {
  style: {
    content: {
      background: 'transparent',
      border: 'none',
      borderRadius: 0,
      overflow: 'visible',
    },
  },
};

type Tick = { second: number; phaseMs: number };

const readTick = (start: number): Tick => {
  const elapsed = Math.max(0, Date.now() - start);
  return { second: Math.floor(elapsed / 1000) + 1, phaseMs: elapsed % 1000 };
};

/** Circumference of the progress ring (r = 50 in the 120-unit viewBox). */
const RING_LENGTH = 314.16;

export const SlipSubmitTimer = ({ startedAt, seconds = 5 }: Props) => {
  const { t } = useTranslation();
  const gradientId = `slip-timer-${useId().replace(/:/g, '')}`;
  const [mountedAt] = useState(() => Date.now());
  const start = startedAt ?? mountedAt;
  const [firstSecond] = useState(() => readTick(start).second);
  const [tick, setTick] = useState<Tick>(() => readTick(start));

  useEffect(() => {
    let timer: number | undefined;
    const update = () => {
      const next = readTick(start);
      setTick((prev) => (prev.second === next.second ? prev : next));
      // Wake just after the next whole second instead of polling.
      timer = window.setTimeout(update, 1000 - next.phaseMs + 10);
    };
    update();
    return () => window.clearTimeout(timer);
  }, [start]);

  const { second, phaseMs } = tick;
  const total = Math.max(1, Math.round(seconds));
  const shown = (s: number) => Math.max(1, total - s + 1);
  // Odd seconds draw the ring, even seconds erase it, so the arc chases
  // itself round the dial with no jump at the top of each second.
  const drawing = second % 2 === 1;
  const inSync: CSSProperties = { animationDelay: `-${phaseMs}ms` };
  // The digit leaving the dial, while the count still moves.
  const showPrevious =
    second > firstSecond && shown(second - 1) !== shown(second);

  return (
    <div className={styles.card} role="status" aria-live="polite">
      <div className={styles.dial} aria-hidden="true">
        <span key={`flash-${second}`} className={styles.flash} />
        <span key={`pulse-${second}`} className={styles.pulse} />
        <svg className={styles.ring} viewBox="0 0 120 120">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" className={styles.stop1} />
              <stop offset="55%" className={styles.stop2} />
              <stop offset="100%" className={styles.stop3} />
            </linearGradient>
          </defs>
          <circle className={styles.bezel} cx="60" cy="60" r="57" />
          <circle className={styles.track} cx="60" cy="60" r="50" />
          <circle
            key={`sweep-${second}`}
            className={classNames(
              styles.sweep,
              drawing ? styles.sweepDraw : styles.sweepErase
            )}
            cx="60"
            cy="60"
            r="50"
            stroke={`url(#${gradientId})`}
            strokeDasharray={`${RING_LENGTH} ${RING_LENGTH}`}
            style={inSync}
          />
        </svg>
        <div className={styles.readout}>
          <div className={styles.digits}>
            {showPrevious && (
              <span
                key={`out-${second - 1}`}
                className={classNames(styles.digit, styles.digitOut)}
              >
                <span className={styles.digitInk}>{shown(second - 1)}</span>
              </span>
            )}
            <span
              key={`in-${shown(second)}`}
              className={classNames(styles.digit, styles.digitIn)}
            >
              <span className={styles.digitInk}>{shown(second)}</span>
            </span>
          </div>
          <span className={styles.unit}>{t('seconds_short_label')}</span>
        </div>
      </div>
      <div className={styles.caption}>
        <span className={styles.title}>
          {t('slip_submitting_label')}
          <span className={styles.dots} aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        </span>
        <span className={styles.hint}>{t('slip_submitting_hint')}</span>
      </div>
    </div>
  );
};
